/* eslint-disable @typescript-eslint/no-explicit-any */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridRequestQuery } from '@api/index';
import { ColumnMode, DatatableComponent, SelectionType, TableColumn } from '@swimlane/ngx-datatable';
import { BehaviorSubject, Subject, concatMap, debounceTime, delay, distinctUntilChanged, from, iif, mergeMap, of, tap } from 'rxjs';
import { DefaultPaginationSize } from 'src/app/shared/constants/pagination-constants';
import { PaginationResult } from 'src/app/shared/interfaces/pagination-result';
import { ResizeObserverService } from 'src/app/shared/services/resize-observer-service/resize-observer.service';

@Component({
    selector: 'app-standard-data-table',
    templateUrl: './standard-data-table.component.html',
    providers: [ResizeObserverService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class StandardDataTableComponent implements OnInit {
    @ViewChild('dataTable') public dataTable: DatatableComponent;
    @ViewChild('expandableColumnTemplate') public expandableColumnTemplate: TemplateRef<string>;

    private visibleAsSubject$ = new BehaviorSubject<boolean>(true);
    public visible$ = this.visibleAsSubject$.asObservable();

    @Input() public set source(value: PaginationResult) {
        this.paginationResult$.next(value);
    }

    @Input() public set noRecordsMessage(value: string | TemplateRef<any>) {
        if (value instanceof TemplateRef) {
            this.noRecordsMessageAsTemplate = value;
            return;
        }

        this.noRecordsMessageAsString = value;
    }

    @Input() public sorts: any = [];
    @Input() public expandableRowContent: TemplateRef<string>;
    @Input() public selectionType: SelectionType;
    @Input() public columns: Array<TableColumn>;
    @Input() public externalPaging = true;
    @Input() public externalSorting = true;
    @Input() public pageNumber = 0;
    @Input() public isSearchHidden = false;

    @Output() public rowSelected = new EventEmitter<any>();
    @Output() public gridRequested = new EventEmitter<GridRequestQuery>();

    public ColumnMode = ColumnMode;
    public currentPage: number;
    public noRecordsMessageAsTemplate: TemplateRef<any>;
    public noRecordsMessageAsString: string;
    public totalItems = 0;
    public rows: any[];

    private paginationResult$ = new Subject<PaginationResult>();
    private pageChanges$ = new Subject<GridRequestQuery>();
    private searchText: string;
    private orderBy: string;
    private orderDirection: string;
    private destroyRef = inject(DestroyRef);
    private cdRef = inject(ChangeDetectorRef);
    private resizeObserverService = inject(ResizeObserverService);

    public ngOnInit(): void {
        this.subscribePageChanges();
        this.subscribePaginationResult();
    }

    public changePage(event: { offset: number; pageSize: number }) {
        const pageSize = event.pageSize > 0 ? event.pageSize : DefaultPaginationSize;
        this.pageNumber = event.offset;
        this.currentPage = this.pageNumber + 1;
        const gridRequest = { page: this.currentPage, pageSize: pageSize, orderBy: this.orderBy, orderByDirection: this.orderDirection, filter: this.searchText } as GridRequestQuery;
        this.pageChanges$.next(gridRequest);
    }

    public sort(event: any) {
        this.orderBy = event.column.prop;
        this.orderDirection = event.newValue;
        const gridRequest = { page: 1, pageSize: DefaultPaginationSize, orderBy: this.orderBy, orderByDirection: this.orderDirection, filter: this.searchText } as GridRequestQuery;
        this.gridRequested.emit(gridRequest);
    }

    public search(searchText: string) {
        this.searchText = searchText;
        const gridRequest = { page: 1, pageSize: DefaultPaginationSize, orderBy: this.orderBy, orderByDirection: this.orderDirection, filter: searchText } as GridRequestQuery;
        this.gridRequested.emit(gridRequest);
    }

    public toggleExpandRow(row: any) {
        this.dataTable.rowDetail.toggleExpandRow(row);
    }

    public selectRow(selected: any) {
        this.rowSelected.emit(selected);
    }

    private subscribePageChanges() {
        this.pageChanges$
            .pipe(
                debounceTime(10),
                distinctUntilChanged((prev, curr) => prev.page === curr.page),
                mergeMap((gridRequestQuery) =>
                    iif(
                        () => {
                            const totalPages = Math.ceil(this.totalItems / gridRequestQuery.pageSize);
                            const isPreviousPageSameAsFirstPage = gridRequestQuery.page - 1 === 1;
                            const isLastPageRequested = totalPages === gridRequestQuery.page;
                            const shouldPreviousPageBeRequestedFirst = isLastPageRequested && !isPreviousPageSameAsFirstPage;
                            return shouldPreviousPageBeRequestedFirst;
                        },
                        from([{ ...gridRequestQuery, page: gridRequestQuery.page - 1 }, gridRequestQuery]),
                        of(gridRequestQuery)
                    )
                ),
                concatMap((gridRequestQuery) => of(gridRequestQuery).pipe(delay(250))),
                tap((gridRequestQuery) => {
                    this.gridRequested.emit(gridRequestQuery);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private subscribePaginationResult() {
        this.paginationResult$
            .pipe(
                debounceTime(10),
                distinctUntilChanged(),
                tap((result) => {
                    if (!result?.items?.length) {
                        this.visibleAsSubject$.next(false);
                        return;
                    }

                    this.visibleAsSubject$.next(true);
                    this.totalItems = result?.pages?.totalItems;

                    const rows = this.mergeRows(result);

                    this.rows = rows;

                    this.recalculateDataTableDimension();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private mergeRows(result: PaginationResult) {
        if (!this.rows?.length) {
            this.rows = new Array(this.totalItems ?? 0);
        }

        const start = (result?.pages?.currentPage - 1) * result?.pages?.size;
        const rows = [...this.rows];
        rows.splice(start, result?.pages?.size, ...result.items);
        return rows;
    }

    private recalculateDataTableDimension() {
        this.resizeObserverService.observe(this.dataTable?.element, () => {
            this.dataTable?.recalculate();
            this.cdRef.detectChanges();
        });
    }
}
